import React, { ReactElement } from 'react';

import {
	getCachedDateTimeFormat,
	getDaysInMonth,
	getLocalDayOfWeek,
	getLocaleMonth,
	getWeekNumberISO8601,
} from '../helpers/dateHelper';

import { DateSetup } from '@interfaces/ganttTasks/dateSetup';

import { EViewMode } from '@enums/ganttTasks';

import TopPartOfCalendar from './TopPartOfCalendar';

import styles from './Calendar.module.scss';

// Y position where the top calendar line starts
const CALENDAR_LINE_START_Y = 10;
// Height of the top part of the calendar
const CALENDAR_TOP_HEIGHT = 23;
// Adjusts text placement inside the top part
const TEXT_TOP_POSITION_RATIO = 0.9;

const TICKS_HALF_DAY = 2;
const TICKS_QUARTER_DAY = 4;

// Base offset for aligning text in the year view
const MONTH_OFFSET = 6;

// Centers text horizontally
const TEXT_CENTER_OFFSET = 0.5;
// Adjusts text vertical position
const TEXT_VERTICAL_RATIO = 0.8;

export type CalendarProps = {
	/**
	 * Provides date-related information for rendering the calendar.
	 * Contains an array of dates to be displayed in the calendar.
	 */
	dateSetup: DateSetup;
	/**
	 * Specifies the locale to be used for formatting dates and months.
	 * E.g., "en-US", "fr-FR", etc.
	 */
	locale: string;
	/**
	 * Defines the view mode of the calendar.
	 * Can be one of the following:
	 * - Year
	 * - QuarterYear
	 * - Month
	 * - Week
	 * - Day
	 * - HalfDay
	 * - QuarterDay
	 * - Hour
	 */
	viewMode: EViewMode;
	headerHeight: number;
	columnWidth: number;
};

const Calendar: React.FC<CalendarProps> = ({
	dateSetup,
	locale,
	viewMode,
	headerHeight,
	columnWidth,
}) => {
	/**
	 * Defines the number of time divisions per day based on the selected view mode.
	 * - Half-day mode splits the day into 2 parts (morning, evening).
	 * - Quarter-day mode splits the day into 4 parts (e.g., every 6 hours).
	 */
	const ticks = viewMode === EViewMode.HalfDay ? TICKS_HALF_DAY : TICKS_QUARTER_DAY;

	const getCalendarValuesForYear = () => {
		const topValues: ReactElement[] = [];
		const bottomValues: ReactElement[] = [];

		for (let i = 0; i < dateSetup.dates.length; i++) {
			const date = dateSetup.dates[i];
			const bottomValue = date.getFullYear();

			bottomValues.push(
				<text
					key={date.getTime()}
					className={styles.calendarBottomText}
					x={columnWidth * i + columnWidth * TEXT_CENTER_OFFSET}
					y={headerHeight * TEXT_VERTICAL_RATIO}
				>
					{bottomValue}
				</text>,
			);

			if (
				i === 0 ||
				date.getFullYear() !== dateSetup.dates[i - 1].getFullYear()
			) {
				const topValue = date.getFullYear().toString();

				// Represents the calculated horizontal position of text on the calendar
				const xText = (MONTH_OFFSET + i - date.getFullYear()) * columnWidth;

				topValues.push(
					<TopPartOfCalendar
						key={topValue}
						value={topValue}
						x1Line={columnWidth * i}
						xText={xText}
						y1Line={CALENDAR_LINE_START_Y}
						y2Line={headerHeight}
						yText={CALENDAR_TOP_HEIGHT * TEXT_TOP_POSITION_RATIO}
					/>,
				);
			}
		}

		return [topValues, bottomValues];
	};

	const getCalendarValuesForQuarterYear = () => {
		const topValues: ReactElement[] = [];
		const bottomValues: ReactElement[] = [];

		for (let i = 0; i < dateSetup.dates.length; i++) {
			const date = dateSetup.dates[i];
			const quarter = 'Q' + Math.floor((date.getMonth() + 3) / 3);

			// Margin for text positioning relative to the calendar line separator
			const textMarginLeft = 4;

			bottomValues.push(
				<text
					key={date.getTime()}
					className={styles.calendarBottomText}
					x={columnWidth * i + columnWidth * TEXT_CENTER_OFFSET}
					y={headerHeight * TEXT_VERTICAL_RATIO}
				>
					{quarter}
				</text>,
			);

			if (
				i === 0 ||
				date.getFullYear() !== dateSetup.dates[i - 1].getFullYear()
			) {
				const topValue = date.getFullYear().toString();

				// Represents the calculated horizontal position of text on the calendar
				const xText = columnWidth * i + textMarginLeft;

				topValues.push(
					<TopPartOfCalendar
						key={topValue}
						value={topValue}
						x1Line={columnWidth * i}
						xText={xText}
						y1Line={CALENDAR_LINE_START_Y}
						y2Line={CALENDAR_TOP_HEIGHT}
						yText={CALENDAR_TOP_HEIGHT * TEXT_TOP_POSITION_RATIO}
					/>,
				);
			}
		}

		return [topValues, bottomValues];
	};

	const getCalendarValuesForMonth = () => {
		const topValues: ReactElement[] = [];
		const bottomValues: ReactElement[] = [];

		for (let i = 0; i < dateSetup.dates.length; i++) {
			const date = dateSetup.dates[i];
			const bottomValue = getLocaleMonth(date, locale);

			bottomValues.push(
				<text
					key={bottomValue + date.getFullYear()}
					className={styles.calendarBottomText}
					x={columnWidth * i + columnWidth * TEXT_CENTER_OFFSET}
					y={headerHeight * TEXT_VERTICAL_RATIO}
				>
					{bottomValue}
				</text>,
			);

			if (
				i === 0 ||
				date.getFullYear() !== dateSetup.dates[i - 1].getFullYear()
			) {
				const topValue = date.getFullYear().toString();

				// Represents the calculated horizontal position of text on the calendar
				const xText = (MONTH_OFFSET + i - date.getMonth()) * columnWidth;

				topValues.push(
					<TopPartOfCalendar
						key={topValue}
						value={topValue}
						x1Line={columnWidth * i}
						xText={xText}
						y1Line={CALENDAR_LINE_START_Y}
						y2Line={CALENDAR_TOP_HEIGHT}
						yText={CALENDAR_TOP_HEIGHT * TEXT_TOP_POSITION_RATIO}
					/>,
				);
			}
		}

		return [topValues, bottomValues];
	};

	const getCalendarValuesForWeek = () => {
		const topValues: ReactElement[] = [];
		const bottomValues: ReactElement[] = [];

		// Counts days since the start of the current month
		let daysSinceMonthStart: number = 1;

		// Margins for text positioning relative to the calendar line separator
		const textMarginLeft = 4;
		const textMarginBottom = 3;

		const dates = dateSetup.dates;

		for (let i = dates.length - 1; i >= 0; i--) {
			const date = dates[i];
			let topValue = '';

			if (i === 0 || date.getMonth() !== dates[i - 1].getMonth()) {
				// top
				topValue = `${getLocaleMonth(date, locale)} ${date.getFullYear()}`;
			}

			// bottom
			const bottomValue = `W${getWeekNumberISO8601(date)}`;

			bottomValues.push(
				<text
					key={date.getTime()}
					className={styles.calendarBottomText}
					x={columnWidth * (i + TEXT_CENTER_OFFSET)}
					y={headerHeight * TEXT_VERTICAL_RATIO}
				>
					{bottomValue}
				</text>,
			);

			if (topValue) {
				// if last day is new month
				if (i !== dates.length - 1) {
					topValues.push(
						<TopPartOfCalendar
							key={topValue}
							value={topValue}
							x1Line={columnWidth * i + daysSinceMonthStart * columnWidth}
							xText={columnWidth * i + textMarginLeft}
							y1Line={CALENDAR_LINE_START_Y}
							y2Line={CALENDAR_TOP_HEIGHT}
							yText={CALENDAR_TOP_HEIGHT - textMarginBottom}
						/>,
					);
				}

				// Reset counter at the start of a new month
				daysSinceMonthStart = 0;
			}

			daysSinceMonthStart++;
		}

		return [topValues, bottomValues];
	};

	const getCalendarValuesForDay = () => {
		const topValues: ReactElement[] = [];
		const bottomValues: ReactElement[] = [];
		const dates = dateSetup.dates;

		for (let i = 0; i < dates.length; i++) {
			const date = dates[i];
			const bottomValue = `${getLocalDayOfWeek(date, locale, 'short')}, ${date
				.getDate()
				.toString()}`;

			bottomValues.push(
				<text
					key={date.getTime()}
					className={styles.calendarBottomText}
					x={columnWidth * i + columnWidth * TEXT_CENTER_OFFSET}
					y={headerHeight * TEXT_VERTICAL_RATIO}
				>
					{bottomValue}
				</text>,
			);

			if (
				i + 1 !== dates.length &&
				date.getMonth() !== dates[i + 1].getMonth()
			) {
				const topValue = getLocaleMonth(date, locale);

				topValues.push(
					<TopPartOfCalendar
						key={topValue + date.getFullYear()}
						value={topValue}
						x1Line={columnWidth * (i + 1)}
						xText={
							columnWidth * (i + 1) -
							getDaysInMonth(date.getMonth(), date.getFullYear()) *
							columnWidth *
							TEXT_CENTER_OFFSET
						}
						y1Line={CALENDAR_LINE_START_Y}
						y2Line={CALENDAR_TOP_HEIGHT}
						yText={CALENDAR_TOP_HEIGHT * TEXT_TOP_POSITION_RATIO}
					/>,
				);
			}
		}

		return [topValues, bottomValues];
	};

	const getCalendarValuesForPartOfDay = () => {
		const topValues: ReactElement[] = [];
		const bottomValues: ReactElement[] = [];
		const dates = dateSetup.dates;

		for (let i = 0; i < dates.length; i++) {
			const date = dates[i];
			const bottomValue = getCachedDateTimeFormat(locale, {
				hour: 'numeric',
			}).format(date);

			bottomValues.push(
				<text
					key={date.getTime()}
					className={styles.calendarBottomText}
					x={columnWidth * i}
					y={headerHeight * TEXT_VERTICAL_RATIO}
				>
					{bottomValue}
				</text>,
			);

			if (i === 0 || date.getDate() !== dates[i - 1].getDate()) {
				const topValue = `${getLocalDayOfWeek(
					date,
					locale,
					'short',
				)}, ${date.getDate()} ${getLocaleMonth(date, locale)}`;

				topValues.push(
					<TopPartOfCalendar
						key={topValue + date.getFullYear()}
						value={topValue}
						x1Line={columnWidth * i + ticks * columnWidth}
						xText={columnWidth * i + ticks * columnWidth * TEXT_CENTER_OFFSET}
						y1Line={CALENDAR_LINE_START_Y}
						y2Line={CALENDAR_TOP_HEIGHT}
						yText={CALENDAR_TOP_HEIGHT * TEXT_TOP_POSITION_RATIO}
					/>,
				);
			}
		}

		return [topValues, bottomValues];
	};

	const getCalendarValuesForHour = () => {
		const topValues: ReactElement[] = [];
		const bottomValues: ReactElement[] = [];
		const dates = dateSetup.dates;

		for (let i = 0; i < dates.length; i++) {
			const date = dates[i];
			const bottomValue = getCachedDateTimeFormat(locale, {
				hour: 'numeric',
			}).format(date);

			bottomValues.push(
				<text
					key={date.getTime()}
					className={styles.calendarBottomText}
					x={columnWidth * i}
					y={headerHeight * TEXT_VERTICAL_RATIO}
				>
					{bottomValue}
				</text>,
			);

			if (i !== 0 && date.getDate() !== dates[i - 1].getDate()) {
				const displayDate = dates[i - 1];
				const topValue = `${getLocalDayOfWeek(
					displayDate,
					locale,
					'long',
				)}, ${displayDate.getDate()} ${getLocaleMonth(displayDate, locale)}`;
				const topPosition = (date.getHours() - 24) / 2;

				topValues.push(
					<TopPartOfCalendar
						key={topValue + displayDate.getFullYear()}
						value={topValue}
						x1Line={columnWidth * i}
						xText={columnWidth * (i + topPosition)}
						y1Line={CALENDAR_LINE_START_Y}
						y2Line={CALENDAR_TOP_HEIGHT}
						yText={CALENDAR_TOP_HEIGHT * TEXT_TOP_POSITION_RATIO}
					/>,
				);
			}
		}

		return [topValues, bottomValues];
	};

	let topValues: ReactElement[] = [];
	let bottomValues: ReactElement[] = [];

	switch (dateSetup.viewMode) {
		case EViewMode.Year:
			[topValues, bottomValues] = getCalendarValuesForYear();
			break;
		case EViewMode.QuarterYear:
			[topValues, bottomValues] = getCalendarValuesForQuarterYear();
			break;
		case EViewMode.Month:
			[topValues, bottomValues] = getCalendarValuesForMonth();
			break;
		case EViewMode.Week:
			[topValues, bottomValues] = getCalendarValuesForWeek();
			break;
		case EViewMode.Day:
			[topValues, bottomValues] = getCalendarValuesForDay();
			break;
		case EViewMode.QuarterDay:
		case EViewMode.HalfDay:
			[topValues, bottomValues] = getCalendarValuesForPartOfDay();
			break;
		case EViewMode.Hour:
			[topValues, bottomValues] = getCalendarValuesForHour();
	}

	return (
		<g
			className='calendar'
			fontSize='10px'
		>
			<rect
				className={styles.calendarHeader}
				height={headerHeight}
				rx='4'
				ry='4'
				width={columnWidth * dateSetup.dates.length}
				x={0}
				y={0}
			/>
			{bottomValues} {topValues}
		</g>
	);
};

export default Calendar;
